/**
 * SU sim core — v0M2.3 tick loop.
 *
 * Per MECHANICS.md §1.1 (deterministic fixed-tick sim): 5 ticks per
 * game-day; sim state mutates ONLY via runTick; all randomness comes
 * from a seeded PRNG owned by the sim; iteration order over entities
 * is by stable id sort (see state.sortedIds); sim has zero imports
 * from client or server code.
 *
 * v0M2.3 wires the fixed system order; individual systems are empty
 * stubs that return state unchanged. Real implementations land per
 * milestone as consuming code arrives.
 */

import type { Command } from './commands';
import type { GameState } from './state';
import type { SimContext } from './sim-context';
import { SYSTEMS } from './systems';

export type { Command } from './commands';
export type {
  GameState,
  FactionId,
  ProvinceId,
  SettlementId,
  ArmyId,
  CohortId,
  CommanderId,
  Faction,
  Province,
  SettlementTier,
  Resources,
  StructureBuild,
  StructureBuildOrder,
  Settlement,
  Cohort,
  Commander,
  Army,
  RecruitmentOrder,
} from './state';
export {
  createEmptyState,
  createEmptyResources,
  createCommander,
  createArmy,
  commanderCohortLimit,
  sortedIds,
  toFactionId,
  toProvinceId,
  toSettlementId,
  toArmyId,
  toCohortId,
  toCommanderId,
  armyEffectiveSpeed,
  DEFAULT_ARMY_SPEED_CELLS_PER_TICK,
  ARRIVAL_EPSILON,
  SECONDS_PER_TICK,
  TICKS_PER_GAME_DAY,
  SECONDS_PER_GAME_DAY,
  MINOR_SETTLEMENT_PILLAGE_DAYS,
  MINOR_SETTLEMENT_REPAIR_DAYS,
  MINOR_SETTLEMENT_REPAIR_GOLD_COST,
  MINOR_SETTLEMENT_STRUCTURE_CAP,
  MINOR_SETTLEMENT_GARRISON_MAX,
} from './state';
export { createRng, snapshotRng } from './rng';
export type { RngState } from './rng';
export { validateCommand } from './commands';
export { SYSTEMS, systemNames } from './systems';
export type { TerrainGrid, ScenarioMapData, ScenarioProvince, ScenarioCity } from './map-data';
export type { SimContext } from './sim-context';
export { computeFactionIncome } from './economy';
export type { VictoryResult } from './state';
export {
  bootstrapScenario,
  NEUTRAL_TEAM_ID,
} from './scenario';
export type {
  Scenario,
  SlotConfig,
  SlotRole,
  StartingMode,
  Difficulty,
  VictoryCondition,
} from './scenario';
export type {
  BattleId,
  Battle,
  Side,
  CohortOnField,
  Figure,
  Square,
  Facing,
  CohortStats,
  StandingOrder,
  BattleResult,
  MoveOrder,
} from './battle-state';
export {
  toBattleId,
  BATTLESCAPE_WIDTH,
  BATTLESCAPE_HEIGHT,
  BATTLES_ROUNDS_PER_TICK,
  COHORT_WIDTH,
  COHORT_DEPTH,
  COHORT_SIZE,
  SIEGE_COHORT_SIZE,
} from './battle-state';
export { advanceBattleRound, advanceBattleSubPhase, checkBattleEnd } from './battle-round';
export {
  BASE_MELEE_TO_HIT,
  BASE_MISSILE_TO_HIT,
  STAMINA_START,
  MORALE_START,
} from './combat-math';
export {
  buildRegionGraph,
  hierarchicalPath,
  terrainCost,
  REGION_SIZE,
} from './pathfind';
export type { RegionGraph, PathResult } from './pathfind';

/**
 * Advance the sim by one tick.
 *
 * Runs each system in SYSTEMS execution order, threading the sim's RNG
 * so any randomness consumed by systems is deterministic given the
 * seed + call sequence. Ends with a tick counter increment.
 *
 * `context` carries per-campaign immutable runtime data (terrain,
 * pathfind graph) — read-only for systems.
 *
 * Callers MUST NOT mutate state outside this function; MECHANICS.md
 * §1.1 rule 1 is the authoritative source.
 */
export function runTick(
  state: GameState,
  commands: readonly Command[],
  context: SimContext,
): GameState {
  let s = state;
  for (const sys of SYSTEMS) {
    s = sys.run(s, commands, s.rng, context);
  }
  return { ...s, tick: s.tick + 1 };
}
